import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { toastError } from "@/lib/errorToast";
import type {
  WeekType,
  PlanningCycleConfig,
  WeekTemplate,
  WeekOverride,
  ComputedWeek,
} from "@/types/weekTypes";
import type { PlanningBlock } from "@/types/planning";
import { blocksOverlap, hydrateBlocksStable } from "@/types/planning";

function generateId() {
  return crypto.randomUUID();
}

function hydrateBlocks(raw: any[]): PlanningBlock[] {
  return hydrateBlocksStable((raw || []).map((b: any) => {
    const { id, ...rest } = b;
    return rest;
  }));
}

function stripIds(blocks: PlanningBlock[]): Omit<PlanningBlock, "id">[] {
  return blocks.map(({ id, ...rest }) => rest);
}

/** Get Monday of the week containing a date */
function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function toISODate(d: Date): string {
  return d.toISOString().split("T")[0];
}

function addWeeks(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n * 7);
  return r;
}

function diffWeeks(a: Date, b: Date): number {
  return Math.floor((a.getTime() - b.getTime()) / (7 * 24 * 60 * 60 * 1000));
}

const DEFAULT_CYCLE: Omit<PlanningCycleConfig, "user_id" | "updated_at"> = {
  enabled: false,
  cycle_length_weeks: 4,
  pattern: ["entreprise", "entreprise", "entreprise", "cours"],
  start_date: toISODate(getMonday(new Date())),
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

export function useCyclePlanning() {
  const { user } = useAuth();
  const [cycle, setCycle] = useState<PlanningCycleConfig | null>(null);
  const [templates, setTemplates] = useState<WeekTemplate[]>([]);
  const [overrides, setOverrides] = useState<WeekOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ── Load ──
  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const [cRes, tRes, oRes] = await Promise.all([
        supabase.from("planning_cycles").select("*").eq("user_id", user.id).maybeSingle(),
        supabase.from("planning_week_templates").select("*").eq("user_id", user.id),
        supabase.from("planning_overrides").select("*").eq("user_id", user.id),
      ]);
      if (cRes.data) setCycle(cRes.data as any);
      if (tRes.data) setTemplates(tRes.data as any);
      if (oRes.data) setOverrides(oRes.data as any);
    } catch (err) {
      console.error("[useCyclePlanning] load error", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // ── Cycle CRUD ──

  const upsertCycle = useCallback(async (patch: Partial<PlanningCycleConfig>) => {
    if (!user) return;
    setSaving(true);
    const payload = {
      user_id: user.id,
      ...DEFAULT_CYCLE,
      ...cycle,
      ...patch,
    };
    const { data, error } = await supabase
      .from("planning_cycles")
      .upsert(payload as any, { onConflict: "user_id" })
      .select()
      .single();
    setSaving(false);
    if (error) { toastError("Erreur sauvegarde cycle", "Modifier le cycle de planification", error.message); console.error(error); return; }
    setCycle(data as any);
    toast.success("Cycle mis à jour");
  }, [user, cycle]);

  const toggleCycle = useCallback(async (enabled: boolean) => {
    await upsertCycle({ enabled });
  }, [upsertCycle]);

  // ── Template CRUD ──

  const upsertTemplate = useCallback(async (weekType: WeekType, blocks: PlanningBlock[]) => {
    if (!user) return;
    setSaving(true);
    const payload = {
      user_id: user.id,
      week_type: weekType,
      blocks: stripIds(blocks),
    };
    const { data, error } = await supabase
      .from("planning_week_templates")
      .upsert(payload as any, { onConflict: "user_id,week_type" })
      .select()
      .single();
    setSaving(false);
    if (error) { toastError("Erreur sauvegarde template", "Sauvegarder un template de semaine", error.message); console.error(error); return; }
    setTemplates((prev) => {
      const idx = prev.findIndex((t) => t.week_type === weekType);
      if (idx >= 0) { const n = [...prev]; n[idx] = data as any; return n; }
      return [...prev, data as any];
    });
    toast.success(`Template "${weekType}" enregistré`);
  }, [user]);

  const getTemplateBlocks = useCallback((weekType: WeekType): PlanningBlock[] => {
    const t = templates.find((t) => t.week_type === weekType);
    return hydrateBlocks(t?.blocks || []);
  }, [templates]);

  const hasTemplate = useCallback((weekType: WeekType): boolean => {
    return templates.some((t) => t.week_type === weekType);
  }, [templates]);

  // ── Override CRUD ──

  const upsertOverride = useCallback(async (
    weekStartDate: string,
    blocks: PlanningBlock[],
    overrideWeekType?: WeekType | null,
    reason?: string
  ) => {
    if (!user) return;
    setSaving(true);
    const payload = {
      user_id: user.id,
      week_start_date: weekStartDate,
      override_blocks: stripIds(blocks),
      override_week_type: overrideWeekType || null,
      reason: reason || null,
    };
    const { data, error } = await supabase
      .from("planning_overrides")
      .upsert(payload as any, { onConflict: "user_id,week_start_date" })
      .select()
      .single();
    setSaving(false);
    if (error) { toastError("Erreur sauvegarde override", "Sauvegarder une exception de semaine", error.message); console.error(error); return; }
    setOverrides((prev) => {
      const idx = prev.findIndex((o) => o.week_start_date === weekStartDate);
      if (idx >= 0) { const n = [...prev]; n[idx] = data as any; return n; }
      return [...prev, data as any];
    });
    toast.success("Exception enregistrée pour cette semaine");
  }, [user]);

  const deleteOverride = useCallback(async (weekStartDate: string) => {
    if (!user) return;
    await supabase
      .from("planning_overrides")
      .delete()
      .eq("user_id", user.id)
      .eq("week_start_date", weekStartDate);
    setOverrides((prev) => prev.filter((o) => o.week_start_date !== weekStartDate));
    toast.success("Exception supprimée");
  }, [user]);

  // ── Computed weeks ──

  const computeWeeks = useCallback((count: number, fromDate?: Date): ComputedWeek[] => {
    if (!cycle?.enabled) return [];
    const startMonday = getMonday(new Date(cycle.start_date));
    const from = fromDate ? getMonday(fromDate) : getMonday(new Date());
    const result: ComputedWeek[] = [];

    for (let i = 0; i < count; i++) {
      const weekDate = addWeeks(from, i);
      const weekStartDate = toISODate(weekDate);
      const weekIdx = diffWeeks(weekDate, startMonday);
      const cyclePos = ((weekIdx % cycle.cycle_length_weeks) + cycle.cycle_length_weeks) % cycle.cycle_length_weeks;
      const baseType = (cycle.pattern[cyclePos] || "entreprise") as WeekType;

      // Check for override
      const override = overrides.find((o) => o.week_start_date === weekStartDate);

      if (override) {
        result.push({
          weekStartDate,
          weekIndex: weekIdx,
          cyclePosition: cyclePos,
          weekType: (override.override_week_type as WeekType) || baseType,
          blocks: override.override_blocks,
          isOverride: true,
          overrideReason: override.reason,
        });
      } else {
        const tmpl = templates.find((t) => t.week_type === baseType);
        result.push({
          weekStartDate,
          weekIndex: weekIdx,
          cyclePosition: cyclePos,
          weekType: baseType,
          blocks: tmpl?.blocks || [],
          isOverride: false,
        });
      }
    }
    return result;
  }, [cycle, templates, overrides]);

  /** Check which week types in pattern are missing templates */
  const missingTemplates = useMemo((): WeekType[] => {
    if (!cycle?.enabled) return [];
    const unique = [...new Set(cycle.pattern)] as WeekType[];
    return unique.filter((wt) => !templates.some((t) => t.week_type === wt));
  }, [cycle, templates]);

  /** Check if there are overrides */
  const hasOverrides = overrides.length > 0;

  /** Get current week status */
  const currentWeekStatus = useMemo(() => {
    if (!cycle?.enabled) return null;
    const weeks = computeWeeks(1);
    if (weeks.length === 0) return null;
    const w = weeks[0];
    return {
      position: w.cyclePosition + 1,
      total: cycle.cycle_length_weeks,
      type: w.weekType,
      isOverride: w.isOverride,
    };
  }, [cycle, computeWeeks]);

  return {
    cycle,
    templates,
    overrides,
    loading,
    saving,
    // Cycle
    upsertCycle,
    toggleCycle,
    // Templates
    upsertTemplate,
    getTemplateBlocks,
    hasTemplate,
    // Overrides
    upsertOverride,
    deleteOverride,
    // Computed
    computeWeeks,
    missingTemplates,
    hasOverrides,
    currentWeekStatus,
    reload: load,
  };
}
